import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import { SimpleOrderContext } from 'pages/order-flow/simple-order/simple-order-context';
import Constructions from '../constructions-step/constructions/constructions.component';
import PatientSummary from '../../shared/confirm/patient-summary/patient-summary.component';
import DeliverySummary from '../../shared/confirm/delivery-summary/delivery-summary.component';
import SampleSummary from '../../shared/confirm/sample-summary/sample-summary.component';
import { createOrderTemplate, modifyOrderTemplate } from 'apollo/templates/order/mutation.graphql';
import { useMutation } from '@apollo/client';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import { useLocation, useNavigate } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import FileGrid from 'components/shared/file-upload-area/file-grid.component';
import { OrderInputType } from 'models/order-input/shared';
import DentistSummary from 'components/order-flow/shared/dentist-summary.component';
import { simpleOrderToOrderInput } from 'components/order-flow/shared/order-input-helper';

const ConfirmStep: FC = () => {
    const { t: translate } = useTranslation(['common', 'orders']);
    const { order } = useContext(SimpleOrderContext);
    const { showBoundary } = useDenthubErrorBoundary();
    const [isResend, setIsResend] = useState(false);
    const search = useLocation().search;
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const { setAppToasterContent } = useContext(AppToasterContext);

    useEffect(() => {
        const queryParams = new URLSearchParams(search);
        setIsResend(queryParams.get('isResend') === 'true' || false);
    }, [search]);

    const [createOrder] = useMutation(createOrderTemplate, {
        onCompleted: ({ createOrder }) => {
            setLoading(false);
            navigate(`/order-details/${createOrder._id}/main-view`);
            setAppToasterContent({ isOpen: true, severity: 'success', message: translate('orders:ORDER_CREATED') });
        },
        onError(error) {
            setLoading(false);
            showBoundary(error);
        },
    });

    const [modifyOrder] = useMutation(modifyOrderTemplate, {
        onCompleted: ({ modifyOrder }) => {
            setLoading(false);
            navigate(`/order-details/${modifyOrder._id}/main-view`);
            setAppToasterContent({ isOpen: true, severity: 'success', message: translate('orders:ORDER_MODIFIED') });
        },
        onError(error) {
            setLoading(false);
            showBoundary(error);
        },
    });

    const getActionButton = () => {
        switch (order.inputType) {
            case OrderInputType.MODIFY:
                return (
                    <LoadingButton
                        loading={loading}
                        disabled={loading}
                        type="button"
                        onClick={() => {
                            setLoading(true);
                            modifyOrder({
                                variables: { orderId: order._id, order: simpleOrderToOrderInput(order), isResend },
                            });
                        }}
                        variant="contained"
                        data-test="confirm-step.modify-submit"
                    >
                        {translate('orders:UPDATE_ORDER')}
                    </LoadingButton>
                );
            case OrderInputType.NEW:
                return (
                    <LoadingButton
                        loading={loading}
                        disabled={loading}
                        type="button"
                        onClick={() => {
                            setLoading(true);
                            createOrder({ variables: { order: simpleOrderToOrderInput(order) } });
                        }}
                        variant="contained"
                        data-test="confirm-step.create-submit"
                    >
                        {translate('orders:REGISTER_ORDER')}
                    </LoadingButton>
                );
            case OrderInputType.WARRANTY:
                return (
                    <LoadingButton
                        loading={loading}
                        disabled={loading}
                        type="button"
                        onClick={() => {
                            // The warranty draft carries the order it is a warranty for; it is completed here.
                            setLoading(true);
                            createOrder({ variables: { order: simpleOrderToOrderInput(order), draftId: order._id } });
                        }}
                        variant="contained"
                        data-test="confirm-step.warranty-submit"
                    >
                        {translate('orders:REGISTER_ORDER')}
                    </LoadingButton>
                );
            default:
                return null;
        }
    };

    return (
        <Fragment>
            {order ? (
                <Box>
                    <Box mb={2}>
                        <Typography variant="h1">{translate('orders:CONFIRM')}</Typography>
                    </Box>
                    <Box mb={2}>
                        <Divider />
                    </Box>
                    <Box mb={2}>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Paper variant="outlined" sx={{ height: '100%' }}>
                                    <Box p={2}>
                                        <Typography variant="h4">{translate('orders:PATIENT')}</Typography>
                                        <Box mt={1}>
                                            <PatientSummary patient={order.patient} />
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Paper variant="outlined" sx={{ height: '100%' }}>
                                    <Box p={2}>
                                        <Typography variant="h4">{translate('orders:DENTIST')}</Typography>
                                        <Box mt={1}>
                                            <DentistSummary dentist={order.dentist} clinic={order.customer} />
                                        </Box>
                                    </Box>
                                </Paper>
                            </Grid>
                        </Grid>
                    </Box>
                    <Box>
                        <Constructions disableEdit />
                    </Box>
                    <Box mb={2}>
                        <Paper variant="outlined">
                            <Box p={2}>
                                <DeliverySummary order={order} />
                            </Box>
                        </Paper>
                    </Box>
                    <Box mb={2}>
                        <Paper variant="outlined">
                            <Box p={2}>
                                <Typography variant="h4">{translate('orders:SAMPLE_AND_PACKAGE_CONTENT')}</Typography>
                                <Box mt={1}>
                                    <SampleSummary sample={order.sample} />
                                </Box>
                            </Box>
                        </Paper>
                    </Box>
                    <Box mb={4}>
                        <FileGrid files={order.files} />
                    </Box>
                    <Box display="flex" justifyContent="center" mb={4}>
                        <Box>{getActionButton()}</Box>
                    </Box>
                    <Box display="flex" justifyContent="center" maxWidth={900} margin="auto">
                        <Typography variant="h6" color="textSecondary" textAlign="center">
                            {translate('orders:ORDER_LEGAL_TEXT')}
                        </Typography>
                    </Box>
                </Box>
            ) : undefined}
        </Fragment>
    );
};

export default ConfirmStep;
